import { PortfolioCurrencyScale } from './portfolio.types';

export const PORTFOLIO_CURRENCY_SCALES: PortfolioCurrencyScale[] = [
  1,
  1_000,
  1_000_000,
  1_000_000_000,
];

export const COMMON_CURRENCY_CODES = [
  'EUR', 'USD', 'GBP', 'CHF', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF',
  'RON', 'BGN', 'JPY', 'CNY', 'HKD', 'SGD', 'AUD', 'CAD', 'NZD', 'INR',
  'BRL', 'MXN', 'AED', 'SAR', 'ZAR',
];

export interface CurrencyUnitSource {
  unit?: string | null;
  currencyCode?: string | null;
  scale?: number | null;
}

export function normalizeCurrencyCode(value: string | null | undefined): string {
  const normalized = value?.trim().toUpperCase() ?? '';
  return /^[A-Z]{3}$/.test(normalized) ? normalized : '';
}

export function normalizeCurrencyScale(value: number | null | undefined): PortfolioCurrencyScale | null {
  const numericValue = Number(value);
  return PORTFOLIO_CURRENCY_SCALES.includes(numericValue as PortfolioCurrencyScale)
    ? numericValue as PortfolioCurrencyScale
    : null;
}

export function resolveCurrencyUnit(source: CurrencyUnitSource, fallbackCurrency = 'USD') {
  const legacy = parseLegacyCurrencyUnit(source.unit);
  return {
    currencyCode: normalizeCurrencyCode(source.currencyCode)
      || legacy?.currencyCode
      || normalizeCurrencyCode(fallbackCurrency)
      || 'USD',
    scale: normalizeCurrencyScale(source.scale) ?? legacy?.scale ?? 1,
  };
}

export function canonicalCurrencyUnit(currencyCode: string, scale: number): string {
  const currency = normalizeCurrencyCode(currencyCode) || 'USD';
  switch (normalizeCurrencyScale(scale) ?? 1) {
    case 1_000:
      return `${currency} '000`;
    case 1_000_000:
      return `${currency} m`;
    case 1_000_000_000:
      return `${currency} bn`;
    default:
      return currency;
  }
}

export function currencyScaleLabel(scale: number): string {
  switch (normalizeCurrencyScale(scale) ?? 1) {
    case 1_000:
      return 'Thousands';
    case 1_000_000:
      return 'Millions';
    case 1_000_000_000:
      return 'Billions';
    default:
      return 'Units';
  }
}

export function formatMoney(
  value: number | null | undefined,
  currencyCode: string,
  compact = false,
): string {
  if (value == null || !Number.isFinite(value)) {
    return '-';
  }
  const currency = normalizeCurrencyCode(currencyCode);
  try {
    return new Intl.NumberFormat('en-US', {
      ...(currency ? { style: 'currency' as const, currency } : {}),
      notation: compact ? 'compact' : 'standard',
      maximumFractionDigits: compact ? 1 : 0,
    }).format(value);
  } catch {
    const formatted = new Intl.NumberFormat('en-US', {
      notation: compact ? 'compact' : 'standard',
      maximumFractionDigits: compact ? 1 : 0,
    }).format(value);
    return currency ? `${currency} ${formatted}` : formatted;
  }
}

function parseLegacyCurrencyUnit(unit: string | null | undefined): {
  currencyCode: string;
  scale: PortfolioCurrencyScale;
} | null {
  const normalized = unit?.trim().replace(/[’`]/g, "'") ?? '';
  const match = normalized.match(/^([A-Za-z]{3})(?:\s+(.+))?$/);
  const currencyCode = normalizeCurrencyCode(match?.[1]);
  if (!match || !currencyCode) {
    return null;
  }
  const token = (match[2] ?? '').trim().toLowerCase().replace(/[,\s']/g, '');
  if (!token || ['unit', 'units'].includes(token)) {
    return { currencyCode, scale: 1 };
  }
  if (['000', 'k', 'thousand', 'thousands'].includes(token)) {
    return { currencyCode, scale: 1_000 };
  }
  if (['m', 'mn', 'mm', 'million', 'millions'].includes(token)) {
    return { currencyCode, scale: 1_000_000 };
  }
  if (['b', 'bn', 'billion', 'billions'].includes(token)) {
    return { currencyCode, scale: 1_000_000_000 };
  }
  return null;
}
